/**
 * PreviewRoot — Main preview container that renders all enabled components
 */
import { computePreviewTokens } from '@/utils/preview-tokens';
import { componentRenderers } from './componentRenderers';

interface PreviewRootProps {
  components: Record<string, any>;
  order: string[];
  settings: any;
  onToggleDarkMode: () => void;
}

export function PreviewRoot({ components, order, settings, onToggleDarkMode }: PreviewRootProps) {
  const rootStyle = computePreviewTokens(settings);

  // Build class names including Premium FX
  const intensity = settings.fxIntensity || "medium";
  const rootClasses = [
    "preview-root",
    settings.darkMode ? "dark" : "",
    settings.fontStack !== "system" ? `font-${settings.fontStack}` : "",
    // Premium FX Classes
    settings.fxOrbs ? "fx-orbs" : "",
    settings.fxOrbs ? `fx-orbs-${intensity}` : "",
    settings.fxGrain ? "fx-grain" : "",
    settings.fxGrain ? `fx-grain-${intensity}` : "",
    settings.fxGlass ? "fx-glass-cards" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClasses} style={rootStyle as any}>
      {/* Grain Overlay Element */}
      {settings.fxGrain && (
        <div className="fx-grain-overlay" aria-hidden="true" />
      )}

      {order.map((id) => {
        const component = components[id];
        if (!component?.enabled) return null;

        const Renderer = componentRenderers[component.type];
        if (!Renderer) return null;

        // Pass onToggleDarkMode only to header
        const extraProps =
          component.type === "header" ? { onToggleDarkMode } : {};

        return (
          <Renderer
            key={id}
            data={component.data}
            settings={settings}
            {...extraProps}
          />
        );
      })}
    </div>
  );
}
